import { Pause, Play } from 'lucide-react';
import { Slider as SliderPrimitive } from 'radix-ui';
import { useTranslation } from 'react-i18next';

import { cn } from '~/lib/utils';

/**
 * Decorative. We have no peak data for a recording, so this is the design's
 * shape reused for every call — it shows position, not loudness. Bars stretch
 * to fill, so the count is a density rather than a width.
 */
const WAVEFORM_HEIGHTS = [
  9, 14, 13, 14, 9, 8, 12, 14, 15, 13, 8, 12, 13, 16, 9, 6, 15, 12, 11, 13, 9,
  9, 12, 12, 15, 15, 9, 9, 12, 15, 9, 11,
];

/** Takes its colour from the card around it, so there is no tone to pass. */
export function CallRecordingPlayer({
  isPlaying,
  progress,
  togglePlay,
  onSeekRatio,
  disabled = false,
  seekDisabled = false,
}: {
  isPlaying: boolean;
  progress: number;
  togglePlay: () => void;
  onSeekRatio: (ratio: number) => void;
  disabled?: boolean;
  seekDisabled?: boolean;
}) {
  const { t } = useTranslation('tasks');
  const played = Math.min(1, Math.max(0, progress));

  return (
    <div className="flex w-full items-center gap-2 rounded-md bg-current/10 p-2">
      <button
        type="button"
        onClick={togglePlay}
        disabled={disabled}
        aria-label={isPlaying ? t('Pause') : t('Play')}
        className="flex size-4 shrink-0 cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isPlaying && <Pause className="size-4 fill-current" />}
        {!isPlaying && <Play className="size-4 fill-current" />}
      </button>
      <SliderPrimitive.Root
        aria-label={t('Seek')}
        value={[played * 100]}
        max={100}
        step={0.1}
        disabled={seekDisabled}
        onValueChange={([value]) => onSeekRatio((value ?? 0) / 100)}
        className="relative flex h-4 min-w-0 flex-1 cursor-pointer touch-none select-none items-center data-disabled:cursor-not-allowed data-disabled:opacity-50"
      >
        <SliderPrimitive.Track className="flex h-4 min-w-0 flex-1 items-center gap-0.5">
          {WAVEFORM_HEIGHTS.map((height, index) => (
            <span
              key={index}
              style={{ height }}
              className={cn(
                'min-w-0 flex-1 rounded-full',
                index / WAVEFORM_HEIGHTS.length < played
                  ? 'bg-current'
                  : 'bg-current/30',
              )}
            />
          ))}
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="block h-4 w-0.5 rounded-full bg-current outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      </SliderPrimitive.Root>
    </div>
  );
}

/** `mm:ss` for player + transcript timestamps. */
export function formatTimestamp(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);

  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}
